import React, { createContext, useContext, useReducer } from 'react';
import type { ReactNode, SyntheticEvent } from 'react';
import type { Action, ActionFns } from './actions';
import { currentTab } from './actions/layout_actions';
import type {
  AllApplicationsRightbar,
  RightBarTabOption,
} from './actions/layout_actions';
import type {
  Header,
  Paths,
  CurrentUser,
  CandidateDetails,
  ScorecardData,
  Decision,
  Offer as OfferType,
  ActivityFeedState,
  CandidateName,
  DocumentInterface,
  MarkAsHired,
  Agreement,
  UploadingDocument,
  IdentityVerification,
} from '../shared/types';
import allReducers from './reducers';
import allActions from './actions';
import { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import { CandidateOffers, OfferLetterTemplate } from '../offers/types';
import { Status } from './actions/status_actions';
import {
  getNoteAnchor,
  rightBarOpenKey,
  rightBarTabKey,
  sortByItems,
  cleanupLocalStorage,
} from '../shared/utils';
import { RejectionCategory } from '../modals/reject/reject_modal';

export type { Action };

// eslint-disable-next-line no-shadow
enum TabNames {
  stage = 'stage',
  scorecard = 'scorecard',
  offer = 'offer',
  activity_feed = 'activity_feed',
  linkedin = 'linkedin',
}

export type TabName = keyof typeof TabNames;
export { TabNames };

export type GlobalModalKeys =
  | 'requestAvailability'
  | 'selfSchedule'
  | 'calendly'
  | 'cancelSelfSchedule'
  | 'createOffer'
  | 'documentViewer'
  | 'virusConfirmationModal'
  | 'cancelScheduledActionModal'
  | 'uploadOfferDocuments'
  | 'generateOfferDocuments'
  | 'rejectModal'
  | 'convertToCandidateModal'
  | 'mergeModal'
  | 'makeProspectModal'
  | 'emailCandidateModal'
  | 'uploadTakeHomeTestModal'
  | 'sendTakeHomeTestModal'
  | 'sendEsignatureModal'
  | 'moveStageModal'
  | 'selectTakeHomeTestStageModal'
  | 'replaceTakeHomeTestModal'
  | 'activeCandidatesConfirmationModal'
  | 'verificationSummaryModal'
  | 'fraudReportModal'
  | 'resolveFraudReportModal';

export type CancelScheduledActionModalProps = {
  cancelPath: string;
  header: string;
  body: string;
  onSuccess?: () => Promise<void>;
} | null;

export type VirusConfirmationModalProps = {
  onConfirm: () => void;
  type: 'virus' | 'scanning' | 'error' | 'skipped';
} | null;

export type UploadOfferDocumentsProps = {
  replacement: boolean;
  restartApprovalWarning: boolean;
  previousOfferLettersWarning: boolean;
  type: 'offer_letter' | 'signed_offer_letter';
} | null;

export type GenerateOfferDocumentsProps = {
  replacement: boolean;
  restartApprovalWarning: boolean;
  offerLetterTemplates: OfferLetterTemplate[];
} | null;

type onClickProps = SyntheticEvent<HTMLButtonElement, Event> | null;
export type MergeModalProps = { leaveSeparate: boolean } | null;

export type GlobalModal = {
  [key in GlobalModalKeys]: key extends 'cancelScheduledActionModal'
    ? [boolean, CancelScheduledActionModalProps]
    : key extends 'documentViewer'
    ? [boolean, DocumentInterface | null]
    : key extends 'virusConfirmationModal'
    ? [boolean, VirusConfirmationModalProps]
    : key extends 'mergeModal'
    ? [boolean, MergeModalProps]
    : key extends 'makeProspectModal'
    ? [boolean, onClickProps]
    : key extends 'uploadOfferDocuments'
    ? [boolean, UploadOfferDocumentsProps]
    : key extends 'rejectModal'
    ? [
        boolean,
        {
          bulkMode: boolean;
          applicationsToRejectInBulkCount: number;
          rejectionCategory?: RejectionCategory;
        }
      ]
    : key extends 'moveStageModal'
    ? [boolean, null]
    : key extends 'activeCandidatesConfirmationModal'
    ? [boolean, null]
    : key extends 'generateOfferDocuments'
    ? [boolean, GenerateOfferDocumentsProps]
    : key extends 'emailCandidateModal'
    ? [boolean, null]
    : key extends 'requestAvailability'
    ? [boolean, { stageId?: number; applicationStageId?: number }]
    : key extends 'selfSchedule'
    ? [boolean, { path: string } | null]
    : key extends 'calendly'
    ? [boolean, { path: string } | null]
    : key extends 'uploadTakeHomeTestModal'
    ? [boolean, { stageId: number | null }]
    : key extends 'sendTakeHomeTestModal'
    ? [boolean, { stageId: number | null; resend: boolean }]
    : key extends 'replaceTakeHomeTestModal'
    ? [boolean, { id: number | null }]
    : key extends 'selectTakeHomeTestStageModal'
    ? [boolean, { documentId: number | null }]
    : key extends 'verificationSummaryModal'
    ? [boolean, { verification: IdentityVerification | null }]
    : key extends 'fraudReportModal'
    ? [boolean, null]
    : key extends 'resolveFraudReportModal'
    ? [boolean, null]
    : [boolean, null];
};

export type State = {
  applicationId: number;
  hiringPlanId: number;
  personId: number;
  candidateName: CandidateName;
  doNotEmail: boolean;
  hasAgencyEmail: boolean;
  currentTab: TabName;
  currentRightBarTab: RightBarTabOption | null | undefined;
  isRightBarOpen: boolean;
  header: Header;
  decision: Decision | null;
  unreadNotesCount: number | null;
  currentUser: CurrentUser;
  paths: Paths;
  activityFeed: ActivityFeedState;
  candidateDetails: CandidateDetails | null;
  graderOptions: Array<Item> | null;
  interviewKitId: number | null;
  scorecards: ScorecardData | null;
  thtLoading: boolean;
  offer: OfferType | null;
  globalModal: GlobalModal;
  candidateOffers: CandidateOffers;
  agreements: Array<Agreement> | null;
  uploadingDocuments: Array<UploadingDocument>;
  status: Status;
  isProspect: boolean;
  expandedOffer: number | null;
  numberOfApplications: number;
  allApplicationsRightbar: AllApplicationsRightbar;
  defaultPoolName: string | null;
  markAsHired: MarkAsHired;
  isLastApplication: boolean;
  dreamJobApplicant: boolean;
};

export type Reducer = (arg1: State, arg2: Action) => State;

export const CandidateProfileContext = createContext<{
  state: State;
  actions: ActionFns;
  paths: Paths;
} | null>(null);

export const tabsKeyedByName = {
  stage: 0,
  scorecard: 1,
  offer: 2,
  activity_feed: 3,
  linkedin: 4,
};

export interface TabsByIndexType {
  [key: string]: string;
}

export const tabsKeyedByIndex: TabsByIndexType = {
  '0': 'stage',
  '1': 'scorecard',
  '2': 'offer',
  '3': 'activity_feed',
  '4': 'linkedin',
};

export const tabMap = Object.assign({}, tabsKeyedByName, tabsKeyedByIndex);

export type Dispatch = (arg1: Action) => void;

export const reduceReducers =
  (reducers: any) => (state: State, action: Action) =>
    reducers.reduce(
      (acc: any, nextReducer: (s: State, a: Action) => State) =>
        nextReducer(acc, action),
      state
    );

export const reduceActions =
  (actions: any) => (state: State, dispatch: Dispatch) =>
    actions.reduce((acc: any, [name, nextAction]: [any, any]) => {
      acc[name] = nextAction(state, dispatch);
      return acc;
    }, {});

export type ProviderProps = {
  children: ReactNode;
  header: Header;
  currentUser: CurrentUser;
  paths: Paths;
  applicationId: number;
  hiringPlanId: number;
  personId: number;
  numberOfUnreadNotes: number | null;
  decision: Decision;
  candidateName: CandidateName;
  doNotEmail: boolean;
  hasAgencyEmail: boolean;
  agreements: Array<Agreement>;
  candidateOffers: CandidateOffers;
  isProspect: boolean;
  defaultPoolName: string | null;
  numberOfApplications: number;
  markAsHired: MarkAsHired;
  isLastApplication: boolean;
  dreamJobApplicant: boolean;
};

type GetInitialState = {
  header: Header;
  currentUser: CurrentUser;
  paths: Paths;
  applicationId: number;
  hiringPlanId: number;
  personId: number;
  numberOfUnreadNotes: number | null;
  candidateDetails?: CandidateDetails;
  decision: Decision;
  candidateName: CandidateName;
  doNotEmail: boolean;
  hasAgencyEmail: boolean;
  candidateOffers: CandidateOffers;
  isProspect: boolean;
  defaultPoolName: string | null;
  numberOfApplications: number;
  markAsHired: MarkAsHired;
  isLastApplication: boolean;
  dreamJobApplicant: boolean;
};

export const getInitialState = ({
  header,
  currentUser,
  paths,
  applicationId,
  hiringPlanId,
  personId,
  numberOfUnreadNotes,
  decision,
  candidateName,
  doNotEmail,
  candidateOffers,
  isProspect,
  defaultPoolName,
  numberOfApplications,
  hasAgencyEmail,
  markAsHired,
  isLastApplication,
  dreamJobApplicant,
}: GetInitialState): State => {
  const url = new URL(window.location.href);
  const inferredApplication = url.searchParams.has('inferred_application');
  const { anchorType } = getNoteAnchor();

  if (numberOfUnreadNotes === null) {
    cleanupLocalStorage('candidate_profile/last_time_visited/');
  }

  if (inferredApplication) {
    localStorage.setItem(rightBarTabKey, 'allJobsAndApplications');
    localStorage.setItem(rightBarOpenKey, true.toString());

    // ensure that refreshing won't keep resetting the user's rightbar tab
    url.searchParams.delete('inferred_application');
    history.replaceState({}, '', url);
  }

  if (anchorType === 'note') {
    localStorage.setItem(rightBarTabKey, 'notes');
    localStorage.setItem(rightBarOpenKey, true.toString());
  }

  // If the user does not have identity verification permissions or fraud report permissions,
  // and the rightbar tab is security screening, set the rightbar open to false
  const canUseSecurityScreening =
    (currentUser.permissions.canViewIdentityVerification ||
      currentUser.permissions.canViewFraudReports) &&
    !isProspect;
  if (
    !canUseSecurityScreening &&
    localStorage.getItem(rightBarTabKey) === 'securityScreening'
  ) {
    localStorage.setItem(rightBarOpenKey, false.toString());
  }

  const isRightBarOpen = localStorage.getItem(rightBarOpenKey) === 'true';
  const currentRightBarTab: any = localStorage.getItem(rightBarTabKey);

  const activityFeed = {
    activityItems: null,
    hasMore: false,
    filters: { sort: sortByItems[0], search: '', types: [] },
    loading: false,
  };

  return {
    applicationId,
    hiringPlanId,
    candidateName,
    personId,
    doNotEmail,
    currentTab: currentTab(),
    currentRightBarTab: isRightBarOpen ? currentRightBarTab : null,
    isRightBarOpen,
    unreadNotesCount: numberOfUnreadNotes,
    header,
    currentUser,
    paths,
    decision,
    offer: null,
    activityFeed,
    candidateDetails: null,
    graderOptions: null,
    interviewKitId: null,
    scorecards: null,
    thtLoading: false,
    isProspect,
    defaultPoolName,
    globalModal: {
      calendly: [false, null],
      cancelSelfSchedule: [false, null],
      requestAvailability: [false, {}],
      selfSchedule: [false, null],
      documentViewer: [false, null],
      virusConfirmationModal: [false, null],
      createOffer: [false, null],
      cancelScheduledActionModal: [false, null],
      uploadOfferDocuments: [false, null],
      generateOfferDocuments: [false, null],
      moveStageModal: [false, null],
      rejectModal: [
        false,
        { bulkMode: false, applicationsToRejectInBulkCount: 0 },
      ],
      convertToCandidateModal: [false, null],
      activeCandidatesConfirmationModal: [false, null],
      mergeModal: [false, null],
      makeProspectModal: [false, null],
      emailCandidateModal: [false, null],
      uploadTakeHomeTestModal: [false, { stageId: null }],
      sendTakeHomeTestModal: [false, { stageId: null, resend: false }],
      sendEsignatureModal: [false, null],
      replaceTakeHomeTestModal: [false, { id: null }],
      selectTakeHomeTestStageModal: [false, { documentId: null }],
      verificationSummaryModal: [false, { verification: null }],
      fraudReportModal: [false, null],
      resolveFraudReportModal: [false, null],
    },
    candidateOffers,
    status: {
      candidateDetails: { type: 'uninitialized' },
      offer: { type: 'uninitialized' },
      scorecard: { type: 'uninitialized' },
      stage: { type: 'complete' }, // Stages are managed by React Query, always complete
      // linkedin requires no async loading currently
      linkedin: { type: 'complete' },
      alertsReminders: { type: 'uninitialized' },
      activity_feed: { type: 'uninitialized' },
      agreements: { type: 'uninitialized' },
      tasks: { type: 'uninitialized' },
    },
    expandedOffer: null,
    numberOfApplications,
    allApplicationsRightbar: {},
    hasAgencyEmail,
    markAsHired,
    agreements: null,
    uploadingDocuments: [],
    isLastApplication,
    dreamJobApplicant,
  };
};

const CandidateProfileProvider = ({
  children,
  header,
  currentUser,
  paths: propsPaths,
  applicationId,
  personId,
  numberOfUnreadNotes,
  decision,
  candidateName,
  doNotEmail,
  candidateOffers,
  isProspect,
  defaultPoolName,
  numberOfApplications,
  hiringPlanId,
  hasAgencyEmail,
  markAsHired,
  isLastApplication,
  dreamJobApplicant,
}: ProviderProps) => {
  const [state, dispatch] = useReducer(
    reduceReducers(Object.values(allReducers)),
    getInitialState({
      header,
      currentUser,
      paths: propsPaths,
      applicationId,
      personId,
      numberOfUnreadNotes,
      decision,
      candidateName,
      doNotEmail,
      candidateOffers,
      isProspect,
      defaultPoolName,
      numberOfApplications,
      hiringPlanId,
      hasAgencyEmail,
      markAsHired,
      isLastApplication,
      dreamJobApplicant,
    })
  );

  const actions = reduceActions(Object.entries(allActions))(state, dispatch);

  return (
    <CandidateProfileContext.Provider
      value={{
        state,
        actions,
        paths: state.paths,
      }}
    >
      {children}
    </CandidateProfileContext.Provider>
  );
};

const useCandidateProfile = (): {
  state: State;
  actions: ActionFns;
  paths: Paths;
} => {
  const context = useContext(CandidateProfileContext);

  if (!context) {
    throw new Error('Not within candidate profile provider');
  }

  return context;
};

export { CandidateProfileProvider, useCandidateProfile };
